import { useEffect, useState } from 'react';
import { Phone as PhoneIcon, Save, X, Edit2, PhoneOutgoing, ChevronDown, ChevronRight, PlusCircle, ArrowRightLeft } from 'lucide-react';
import { phoneNumbersAPI, outboundAPI, readinessAPI } from '../services/api.js';
import { formatPhone, statusBadge } from '../lib/format.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import ProvisionLine from './ProvisionLine.jsx';
import ReassignNumber from './ReassignNumber.jsx';
import {
  ReadinessBadge,
  ReadinessChecks,
  ReadinessUnavailable,
  ReadinessError,
  ScheduleReadyNote,
  sameDid
} from './LineReadiness.jsx';

// Per-phone-number config (Phase 7b): label, custom inbound greeting,
// per-number instructions appended to the system prompt. Plus an inline
// "Test call" button (Phase 5b) that originates from this number.

// `hideActions` drops the Edit + Test-call buttons (used in the simplified
// exec-admin Settings, where the number is view-only).
export default function PhoneNumbersEditor({ hideActions = false }) {
  // Provisioning mutates Telnyx and LiveKit and is owner-only server-side
  // (requireOwner on /admin/provision-line), so don't offer it to anyone else.
  const { isOwner, tenant } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState({}); // id -> draft
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);
  const [testing, setTesting] = useState(null); // { id, to }
  const [testStatus, setTestStatus] = useState('');
  const [readiness, setReadiness] = useState(null);       // the /readiness result
  const [readinessMissing, setReadinessMissing] = useState(false); // core 404 → no verdict
  const [readinessError, setReadinessError] = useState(''); // anything else that failed
  const [readinessLoading, setReadinessLoading] = useState(true);
  const [showWhy, setShowWhy] = useState({});             // did -> bool
  const [adding, setAdding] = useState(false);
  const [moving, setMoving] = useState(null); // phone_numbers row being reassigned

  const load = async () => {
    setLoading(true);
    try {
      const r = await phoneNumbersAPI.list();
      setItems(r?.data || r || []);
    } catch (err) {
      setError(err.message || 'Failed to load phone numbers.');
    } finally {
      setLoading(false);
    }
  };

  // Readiness is loaded SEPARATELY and never blocks the list: it reaches out to
  // LiveKit and Telnyx, so it is the slow, failure-prone half. A readiness error
  // must not surface as "failed to load phone numbers" — the numbers are fine,
  // we just have no verdict for them.
  const loadReadiness = async () => {
    setReadinessLoading(true);
    try {
      const r = await readinessAPI.mine();
      setReadiness(r?.data || r || null);
      setReadinessMissing(false);
      setReadinessError('');
    } catch (err) {
      // 404 = core doesn't have the readiness routes deployed yet. Anything else
      // is a real failure and must SAY so — silence here is indistinguishable
      // from "the feature isn't there", and someone would read it as fine.
      setReadinessMissing(err.status === 404);
      setReadinessError(err.status === 404 ? '' : err.message || 'check failed');
      setReadiness(null);
    } finally {
      setReadinessLoading(false);
    }
  };

  useEffect(() => { load(); loadReadiness(); }, []);

  // Per-DID verdict from `numbers[]`. Absent → render nothing rather than guess;
  // an unearned green badge is exactly the false confidence this column exists
  // to remove. Matched on digits (sameDid) because core answers normalized but
  // the phone_numbers row was not necessarily stored that way.
  const verdictFor = (e164) =>
    (readiness?.numbers || []).find((n) => sameDid(n.did, e164)) || null;

  // DIDs readiness reports on that the phone-numbers list doesn't have. Today
  // that's demonstration lines, which bind straight to a DID and answer with no
  // phone_numbers row at all.
  const extraLiveDids = (readiness?.numbers || []).filter(
    (n) => n.did && !items.some((p) => sameDid(p.phoneNumber, n.did))
  );

  const startEdit = (p) => setEditing((s) => ({
    ...s,
    [p.id]: {
      label: p.configuration?.label || '',
      greeting: p.configuration?.greeting || '',
      customInstructions: p.configuration?.customInstructions || ''
    }
  }));
  const cancelEdit = (id) => setEditing((s) => { const n = { ...s }; delete n[id]; return n; });

  const saveEdit = async (id) => {
    const draft = editing[id];
    if (!draft) return;
    try {
      const payload = {
        label: draft.label.trim() || null,
        greeting: draft.greeting.trim() || null,
        customInstructions: draft.customInstructions.trim() || null
      };
      const r = await phoneNumbersAPI.update(id, payload);
      setItems((list) => list.map((p) => (p.id === id ? r.data : p)));
      cancelEdit(id);
      setSavedAt(new Date());
    } catch (err) {
      setError(err.message || 'Save failed.');
    }
  };

  const startTest = (p) => { setTesting({ id: p.id, to: '' }); setTestStatus(''); };
  const cancelTest = () => { setTesting(null); setTestStatus(''); };
  const placeTest = async () => {
    if (!testing?.to || !/^\+[1-9]\d{6,14}$/.test(testing.to)) {
      setTestStatus('Please enter a destination number in E.164 format (e.g. +13462580006).');
      return;
    }
    setTestStatus('Dialing…');
    try {
      const r = await outboundAPI.place({
        to: testing.to,
        fromNumberId: testing.id,
        intent: 'test'
      });
      setTestStatus(`Call placed (id ${r?.data?.id?.slice(0, 8)}…). Your phone should ring shortly.`);
    } catch (err) {
      setTestStatus(err.message || 'Failed to place test call.');
    }
  };

  return (
    <section className="card">
      <header className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <PhoneIcon size={18} className="text-alfred-gold" />
          <h2 className="text-xl text-stone-100">Phone numbers</h2>
        </div>
        <div className="flex items-center gap-2">
          {savedAt && (
            <span className="text-xs text-stone-500">Saved {savedAt.toLocaleTimeString()}</span>
          )}
          {isOwner && !hideActions && !adding && (
            <button type="button" className="btn-ghost" onClick={() => setAdding(true)}>
              <PlusCircle size={15} /> Add number
            </button>
          )}
        </div>
      </header>

      <p className="text-sm text-stone-400 mb-4">
        Your assistant answers this number. Edit it to set the greeting callers hear
        and any special instructions for how it should handle this line.
      </p>

      {error && (
        <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{error}</p>
      )}

      {adding && (
        <div className="mb-5">
          <ProvisionLine
            tenantId={tenant?.id}
            tenantName={tenant?.companyName}
            onDone={(changed) => {
              setAdding(false);
              // Only re-read Telnyx/LiveKit when something actually changed —
              // readiness is two vendor calls and this closes on cancel too.
              if (changed) { load(); loadReadiness(); }
            }}
          />
        </div>
      )}

      {loading ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-stone-500">
          {isOwner && !hideActions
            ? 'No phone numbers on this account yet — use “Add number” to wire one up.'
            : 'No phone numbers on this account yet. Ask your administrator to add one.'}
        </p>
      ) : (
        <ul className="divide-y divide-stone-900">
          {items.map((p) => {
            const isEditing = !!editing[p.id];
            const isTesting = testing?.id === p.id;
            const verdict = verdictFor(p.phoneNumber);
            const whyOpen = !!showWhy[p.phoneNumber];
            return (
              <li key={p.id} className="py-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-stone-100 font-medium">
                      {formatPhone(p.phoneNumber)}
                      {p.configuration?.label && (
                        <span className="ml-2 text-xs text-stone-400 italic">— {p.configuration.label}</span>
                      )}
                    </div>
                    <div className="text-xs text-stone-500">
                      {(p.numberType === 'tollfree' ? 'Toll-free' : 'Local')} number
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {/* Readiness makes two vendor calls, so it lands after the list.
                        Say so — otherwise the DB `active` badge below sits alone and
                        reads as a verdict it isn't. */}
                    {readinessLoading && !verdict && (
                      <span className="text-xs text-stone-500">Checking…</span>
                    )}
                    {verdict && <ReadinessBadge status={verdict.status} />}
                    <span className={`badge ${statusBadge(p.status)}`}>{p.status || 'unknown'}</span>
                    {!hideActions && !isEditing && !isTesting && (
                      <>
                        <button type="button" className="btn-ghost" onClick={() => startEdit(p)}>
                          <Edit2 size={14} /> Edit
                        </button>
                        <button type="button" className="btn-ghost" onClick={() => startTest(p)}>
                          <PhoneOutgoing size={14} /> Test call
                        </button>
                        {isOwner && (
                          <button type="button" className="btn-ghost" onClick={() => setMoving(p)}>
                            <ArrowRightLeft size={14} /> Move
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* What was checked, in core's own words — available on EVERY line,
                    not only broken ones. A green badge can still sit on top of "a
                    demonstration owned by another tenant answers this number" or
                    "no business prompt, so this line runs the generic one". Hiding
                    those behind the green is the false all-clear we're here to end. */}
                {verdict && (
                  <div className="mt-2">
                    <button
                      type="button"
                      className="flex items-center gap-1 text-xs text-stone-400 hover:text-alfred-gold"
                      onClick={() =>
                        setShowWhy((s) => ({ ...s, [p.phoneNumber]: !s[p.phoneNumber] }))
                      }
                    >
                      {whyOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                      {verdict.status === 'unknown'
                        ? 'Couldn’t verify this line — what was checked'
                        : verdict.status === 'blocked'
                          ? `Not live — ${verdict.blockers?.length || 0} blocker${
                              verdict.blockers?.length === 1 ? '' : 's'
                            }`
                          : 'What was checked'}
                    </button>
                    {whyOpen && (
                      <div className="mt-2 rounded-lg border border-stone-800 bg-stone-950/40 p-3">
                        <ReadinessChecks checks={readiness?.checks} did={p.phoneNumber} />
                      </div>
                    )}
                  </div>
                )}

                {moving?.id === p.id && (
                  <div className="mt-4">
                    <ReassignNumber
                      did={p.phoneNumber}
                      currentTenantName={tenant?.companyName}
                      onDone={(changed) => {
                        setMoving(null);
                        if (changed) { load(); loadReadiness(); }
                      }}
                    />
                  </div>
                )}

                {isEditing && (
                  <div className="mt-4 space-y-3 rounded-lg border border-alfred-gold/30 p-4">
                    <input
                      className="field-input"
                      placeholder="Label (e.g. Sales line)"
                      value={editing[p.id].label}
                      onChange={(e) => setEditing((s) => ({ ...s, [p.id]: { ...s[p.id], label: e.target.value } }))}
                    />
                    <textarea
                      className="field-input min-h-[80px]"
                      placeholder="Greeting — what Alfred says when this number rings. Leave blank to use the tenant default."
                      value={editing[p.id].greeting}
                      onChange={(e) => setEditing((s) => ({ ...s, [p.id]: { ...s[p.id], greeting: e.target.value } }))}
                      maxLength={2000}
                    />
                    <textarea
                      className="field-input min-h-[100px]"
                      placeholder="Per-number instructions — appended to the system prompt for calls on this line. e.g. 'This is the sales line — focus on qualifying interest and book a follow-up.'"
                      value={editing[p.id].customInstructions}
                      onChange={(e) => setEditing((s) => ({ ...s, [p.id]: { ...s[p.id], customInstructions: e.target.value } }))}
                      maxLength={4000}
                    />
                    <div className="flex gap-2">
                      <button type="button" className="btn-primary" onClick={() => saveEdit(p.id)}>
                        <Save size={14} /> Save
                      </button>
                      <button type="button" className="btn-ghost" onClick={() => cancelEdit(p.id)}>
                        <X size={14} /> Cancel
                      </button>
                    </div>
                  </div>
                )}

                {isTesting && (
                  <div className="mt-4 space-y-3 rounded-lg border border-alfred-gold/30 p-4">
                    <p className="text-sm text-stone-300">
                      Place a test call from <span className="text-alfred-gold">{formatPhone(p.phoneNumber)}</span>.
                      Alfred will ring the number you enter and identify himself
                      as a connection check.
                    </p>
                    <input
                      className="field-input"
                      placeholder="+13462580006"
                      value={testing.to}
                      onChange={(e) => setTesting({ ...testing, to: e.target.value })}
                      autoFocus
                    />
                    {testStatus && (
                      <p className="text-xs text-stone-400">{testStatus}</p>
                    )}
                    <div className="flex gap-2">
                      <button type="button" className="btn-primary" onClick={placeTest}>
                        <PhoneOutgoing size={14} /> Place test call
                      </button>
                      <button type="button" className="btn-ghost" onClick={cancelTest}>
                        <X size={14} /> Cancel
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Lines that RING but have no phone-number record — a demonstration binds
          straight to a DID and answers without one. A screen that inventories
          ringing lines must not hide them. */}
      {extraLiveDids.length > 0 && (
        <div className="mt-6 border-t border-stone-900 pt-4">
          <h3 className="mb-2 text-sm text-stone-300">Also answering</h3>
          <p className="mb-3 text-xs text-stone-500">
            These numbers ring on this account without a phone-number record above —
            a demonstration answers them directly.
          </p>
          <ul className="space-y-2">
            {extraLiveDids.map((n) => (
              <li key={n.did} className="flex items-center justify-between gap-3 text-sm">
                <span className="text-stone-200">{formatPhone(n.did)}</span>
                <ReadinessBadge status={n.status} />
              </li>
            ))}
          </ul>
        </div>
      )}

      <ScheduleReadyNote scheduleReady={readiness?.scheduleReady} />

      {(readinessMissing || readinessError) && (
        <div className="mt-4">
          {readinessMissing ? <ReadinessUnavailable /> : <ReadinessError message={readinessError} />}
        </div>
      )}
    </section>
  );
}
